import { Fragment, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  Loader2, History, Stethoscope, X, Printer,
  ArrowDownCircle, ArrowUpCircle, RotateCcw, Receipt, Tag,
  CalendarPlus, ClipboardList, Layers3, WalletCards, CheckCircle2, PenLine,
  Scale,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { buildVisitBreakdown } from "@/lib/visitBreakdown";
import SignatureDialog from "@/components/documents/SignatureDialog";
import {
  DOCUMENT_TYPES,
  createDocumentSnapshot,
  getSignedDocumentStatus,
  signDocumentSnapshot,
  signatureForPdf,
} from "@/lib/signedDocuments";

// =====================================================================
// VisitsTab — read-only completed-visit history per patient.
//
// Source of truth is dos_v2.list_patient_visits + dos_v2.get_visit for
// totals and the append-only money ledger. The detail modal enriches that
// read with existing lineage tables so the user can see the actual Tx Plan
// procedure visits, separate walk-in procedures, future needs created, and
// payment allocations behind the totals.
//
// Voided visits stay in the list with a rose status pill + the void
// reason. Empty visits (checkout closed without any charges or
// payments) still appear; their totals just read ₱0.00.
//
// Click a card → modal calls get_visit and renders the full money-
// events trail (procedure-by-procedure charges, then payments /
// adjustments / refunds / reversals). Read-only. Edits are not
// possible — the underlying tables are append-only.
// =====================================================================

export default function VisitsTab({ patientId, demoVisits = null, demoVisitDetailsById = null }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(() => !demoVisits);
  const [error, setError] = useState(null);
  const [openVisitId, setOpenVisitId] = useState(null);

  useEffect(() => {
    if (demoVisits) {
      setVisits(demoVisits);
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    supabase
      .rpc("list_patient_visits", { p_patient_id: patientId })
      .then(({ data, error: e }) => {
        if (cancelled) return;
        setLoading(false);
        if (e) {
          setError(e.message ?? "Could not load visits.");
          return;
        }
        setVisits(data ?? []);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, demoVisits]);

  if (loading) {
    return (
      <div
        className="bg-white rounded-2xl shadow-sm p-12 text-center"
        data-testid="visits-loading"
      >
        <Loader2 className="w-5 h-5 mx-auto animate-spin text-slate-400" />
        <p className="mt-3 text-sm text-slate-500">Loading visits…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-sm text-rose-700"
        data-testid="visits-error"
      >
        {error}
      </div>
    );
  }

  if (visits.length === 0) {
    return (
      <div
        className="bg-white rounded-2xl shadow-sm p-12 text-center"
        data-testid="visits-empty"
      >
        <div className="w-12 h-12 rounded-full bg-slate-100 mx-auto flex items-center justify-center">
          <History className="w-5 h-5 text-slate-400" />
        </div>
        <h3 className="mt-4 text-base font-semibold text-slate-900">
          No completed visits yet
        </h3>
        <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
          Once an appointment is checked in and checkout is finalized,
          the visit will appear here with its charges and payments.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="visits-tab">
      {visits.map((v) => (
        <VisitCard
          key={v.id}
          visit={v}
          onView={() => setOpenVisitId(v.id)}
        />
      ))}
      {openVisitId ? (
        <VisitDetailDialog
          visitId={openVisitId}
          demoVisit={demoVisitDetailsById?.[openVisitId] ?? null}
          onClose={() => setOpenVisitId(null)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// balanceLabel — turn a signed centavo balance into plain words + tone.
// Positive = the patient owes; negative = clinic holds a credit; zero = clear.
// No green minus signs — a credit reads as "Credit ₱X", not "−₱X".
// ---------------------------------------------------------------------
function balanceLabel(centavos) {
  const n = centavos ?? 0;
  if (n > 0) return { label: "Still owes", value: formatPHP(n), tone: "owe" };
  if (n < 0) return { label: "Credit", value: formatPHP(-n), tone: "credit" };
  return { label: "Paid in full", value: formatPHP(0), tone: "neutral" };
}

// ---------------------------------------------------------------------
// VisitCard — a real treatment day (procedures were done).
// A checkout that only took a payment renders as PaymentRow instead.
// ---------------------------------------------------------------------
function VisitCard({ visit, onView }) {
  const isVoided = visit.status === "Voided";
  const date = visit.finalized_at ?? visit.voided_at ?? visit.created_at;
  // "Did work happen?" counts clinical procedures performed — including
  // plan/MV work rendered at ₱0 (charged once at acceptance, not per visit).
  // A true payment drop-off has no work AND no charge, just money in.
  const doneCount = (visit.procedure_count ?? 0) + (visit.work_count ?? 0);
  const isPaymentOnly =
    !isVoided &&
    doneCount === 0 &&
    (visit.charges_centavos ?? 0) === 0 &&
    (visit.payments_centavos ?? 0) > 0;

  if (isPaymentOnly) return <PaymentRow visit={visit} date={date} onView={onView} />;

  const bal = balanceLabel(visit.balance_centavos);

  return (
    <button
      type="button"
      onClick={onView}
      data-testid={`visit-card-${visit.id}`}
      className={[
        "w-full text-left bg-white rounded-2xl shadow-sm p-4 hover:shadow-md transition-shadow border",
        isVoided ? "border-rose-200" : "border-transparent",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="font-semibold text-slate-900">
              {formatDate(date)}
            </span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-700">{formatTime(date)}</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-700 inline-flex items-center gap-1">
              <Stethoscope className="w-3.5 h-3.5 text-slate-400" />
              {visit.doctor_name}
            </span>
          </div>
          {Array.isArray(visit.procedures) && visit.procedures.length > 0 ? (
            <ul className="mt-1.5 space-y-1">
              {visit.procedures.map((proc, i) => (
                <li
                  key={i}
                  className="text-xs text-slate-600 flex items-baseline gap-1.5 flex-wrap"
                >
                  <span className="font-medium text-slate-800">{proc.name}</span>
                  {proc.seq ? (
                    <span className="text-slate-400">
                      · Visit {proc.seq}
                      {proc.label ? ` · ${proc.label}` : ""}
                    </span>
                  ) : proc.label ? (
                    <span className="text-slate-400">· {proc.label}</span>
                  ) : null}
                  {proc.tooth ? (
                    <span className="text-slate-400">· #{proc.tooth}</span>
                  ) : null}
                </li>
              ))}
              {visit.notes ? (
                <li className="text-xs text-slate-400">
                  {truncate(visit.notes, 80)}
                </li>
              ) : null}
            </ul>
          ) : (
            <p className="mt-1 text-xs text-slate-500">
              {doneCount}{" "}
              {doneCount === 1 ? "procedure" : "procedures"}
              {visit.notes ? ` · ${truncate(visit.notes, 80)}` : ""}
            </p>
          )}
          {isVoided && visit.void_reason ? (
            <p className="mt-1 text-xs text-rose-700">
              Voided: {visit.void_reason}
            </p>
          ) : null}
        </div>
        <StatusPill voided={isVoided} />
      </div>

      {!isVoided ? (
        <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
          <Stat label="Charged" value={formatPHP(visit.charges_centavos)} />
          <Stat label="Paid" value={formatPHP(visit.payments_centavos)} />
          <Stat label={bal.label} value={bal.value} tone={bal.tone} />
        </div>
      ) : null}
    </button>
  );
}

// ---------------------------------------------------------------------
// PaymentRow — a checkout that only took money (no clinical work). Slim
// by design so it reads as a payment drop-off, never a "0 procedures" visit.
// ---------------------------------------------------------------------
function PaymentRow({ visit, date, onView }) {
  return (
    <button
      type="button"
      onClick={onView}
      data-testid={`visit-payment-row-${visit.id}`}
      className="w-full text-left bg-white rounded-2xl shadow-sm px-4 py-3 hover:shadow-md transition-shadow border border-transparent flex items-center gap-3"
    >
      <span className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
        <ArrowDownCircle className="w-5 h-5 text-emerald-600" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-900">
          Payment received — {formatPHP(visit.payments_centavos)}
        </span>
        <span className="block text-xs text-slate-500">
          {formatDate(date)} · {formatTime(date)}
          {visit.notes ? ` · ${truncate(visit.notes, 60)}` : ""}
        </span>
      </span>
      <span className="text-[11px] text-slate-400 shrink-0">No treatment</span>
    </button>
  );
}

function StatusPill({ voided }) {
  if (voided) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-50 text-rose-700 border border-rose-200 shrink-0">
        Voided
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-teal-50 text-teal-700 border border-teal-200 shrink-0">
      Finalized
    </span>
  );
}

function Stat({ label, value, tone = "neutral" }) {
  const toneClass =
    tone === "owe"
      ? "text-rose-600"
      : tone === "credit"
      ? "text-emerald-600"
      : "text-slate-900";
  return (
    <div>
      <p className="text-[11px] text-slate-400 uppercase tracking-wide">
        {label}
      </p>
      <p className={`text-sm font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------
// VisitDetailDialog
// ---------------------------------------------------------------------
function VisitDetailDialog({ visitId, onClose, demoVisit = null }) {
  const [visit, setVisit] = useState(demoVisit);
  const [loading, setLoading] = useState(() => !demoVisit);
  const [error, setError] = useState(null);
  const [enrichment, setEnrichment] = useState(() => demoVisit?.enrichment ?? null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [enrichmentError, setEnrichmentError] = useState(null);
  const [buckets, setBuckets] = useState(() => demoVisit?.buckets ?? null);

  useEffect(() => {
    if (demoVisit) {
      setVisit(demoVisit);
      setEnrichment(demoVisit.enrichment ?? null);
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    supabase
      .rpc("get_visit", { p_visit_id: visitId })
      .then(({ data, error: e }) => {
        if (cancelled) return;
        setLoading(false);
        if (e) {
          setError(e.message ?? "Could not load visit.");
          return;
        }
        setVisit(data);
      });
    return () => {
      cancelled = true;
    };
  }, [visitId, demoVisit]);

  useEffect(() => {
    if (!visit || demoVisit) return undefined;
    let cancelled = false;
    setEnrichmentLoading(true);
    setEnrichmentError(null);
    fetchVisitEnrichment(visit)
      .then((data) => {
        if (cancelled) return;
        setEnrichment(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setEnrichment(null);
        setEnrichmentError(e?.message ?? "Could not load visit breakdown.");
      })
      .finally(() => {
        if (!cancelled) setEnrichmentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visit, demoVisit]);

  // Account-standing buckets (TP / MV / Ortho / Walk-in + total owed).
  // Read-only RPC; the balance math lives in the database.
  useEffect(() => {
    if (demoVisit) return undefined;
    let cancelled = false;
    supabase
      .rpc("visit_balance_buckets", { p_visit_id: visitId })
      .then(({ data, error: e }) => {
        if (cancelled) return;
        setBuckets(e ? null : data);
      });
    return () => {
      cancelled = true;
    };
  }, [visitId, demoVisit]);

  // Close on Escape so the dialog matches the rest of the app's
  // keyboard expectations (mirrors the note dialog patterns).
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="visit-detail-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="visit-detail-dialog"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 sticky top-0 bg-white">
          <h2 className="text-base font-semibold text-slate-900">
            Visit details
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
            aria-label="Close"
            data-testid="visit-detail-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-5 h-5 mx-auto animate-spin text-slate-400" />
            <p className="mt-3 text-sm text-slate-500">Loading…</p>
          </div>
        ) : error ? (
          <div className="m-5 bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700">
            {error}
          </div>
        ) : visit ? (
          <DetailContent
            visit={visit}
            enrichment={enrichment}
            enrichmentLoading={enrichmentLoading}
            enrichmentError={enrichmentError}
            buckets={buckets}
          />
        ) : null}
      </div>
    </div>
  );
}

async function fetchVisitEnrichment(visit) {
  const txPlanItemsDone = await fetchTxPlanItemsDone(visit);
  const [futureNeedsPlaced, paymentAllocations] = await Promise.all([
    fetchFutureNeedsPlaced(visit, txPlanItemsDone),
    fetchPaymentAllocations(visit),
  ]);
  return { txPlanItemsDone, futureNeedsPlaced, paymentAllocations };
}

async function fetchTxPlanItemsDone(visit) {
  const rowsById = new Map();
  const baseSelect = "id, treatment_plan_item_id, sequence, template_label, appointment_id, visit_id, completed_at, outcome, chair_min, phase_number";

  if (visit?.appointment_id) {
    const { data, error } = await supabase
      .from("procedure_visits")
      .select(baseSelect)
      .eq("appointment_id", visit.appointment_id);
    if (error) throw error;
    for (const row of data || []) rowsById.set(row.id, row);
  }

  if (visit?.id) {
    const { data, error } = await supabase
      .from("procedure_visits")
      .select(baseSelect)
      .eq("visit_id", visit.id);
    if (error) throw error;
    for (const row of data || []) rowsById.set(row.id, row);
  }

  const procedureVisits = Array.from(rowsById.values())
    .filter((row) => row.completed_at || row.outcome === "done");
  if (procedureVisits.length === 0) return [];

  const itemIds = unique(procedureVisits.map((row) => row.treatment_plan_item_id).filter(Boolean));
  if (itemIds.length === 0) return [];

  const { data: itemsRaw, error: itemsError } = await supabase
    .from("treatment_plan_items")
    .select("id, treatment_plan_id, procedure_id, tooth_number, surface, fee_centavos, committed_fee_centavos, name_snapshot, teeth, unit_count, material")
    .in("id", itemIds);
  if (itemsError) throw itemsError;

  const itemsById = new Map((itemsRaw || []).map((item) => [item.id, item]));
  const planIds = unique((itemsRaw || []).map((item) => item.treatment_plan_id).filter(Boolean));
  const procedureIds = unique((itemsRaw || []).map((item) => item.procedure_id).filter(Boolean));

  const [plansById, proceduresById] = await Promise.all([
    fetchPlansById(planIds),
    fetchProceduresById(procedureIds),
  ]);

  return procedureVisits
    .map((row) => {
      const item = itemsById.get(row.treatment_plan_item_id) || {};
      const plan = plansById.get(item.treatment_plan_id) || {};
      const procedure = proceduresById.get(item.procedure_id) || {};
      return {
        id: row.id,
        procedure_visit_id: row.id,
        treatment_plan_item_id: row.treatment_plan_item_id,
        treatment_plan_id: item.treatment_plan_id || null,
        plan_code: plan.plan_code || "Tx Plan",
        plan_status: plan.status || null,
        procedure_name: item.name_snapshot || procedure.name || "Tx plan item",
        template_label: row.template_label || null,
        sequence: row.sequence || null,
        phase_number: row.phase_number || null,
        completed_at: row.completed_at || null,
        outcome: row.outcome || null,
        chair_min: row.chair_min || null,
        tooth_number: item.tooth_number || null,
        surface: item.surface || null,
        teeth: Array.isArray(item.teeth) ? item.teeth : [],
        unit_count: item.unit_count || null,
        material: item.material || null,
        fee_centavos: Number(item.committed_fee_centavos ?? item.fee_centavos ?? 0),
      };
    })
    .sort((a, b) => {
      const planSort = String(a.plan_code).localeCompare(String(b.plan_code));
      if (planSort !== 0) return planSort;
      return Number(a.sequence || 0) - Number(b.sequence || 0)
        || String(a.procedure_name).localeCompare(String(b.procedure_name));
    });
}

async function fetchFutureNeedsPlaced(visit, txPlanItemsDone) {
  const needsById = new Map();
  const sourceProcedureVisitIds = unique(
    (txPlanItemsDone || []).map((row) => row.procedure_visit_id).filter(Boolean),
  );
  const selectFields = "id, procedure_id, treatment_plan_id, treatment_plan_item_id, scheduled_appointment_id, tooth_number, surface, status, completed_at, completed_via_visit_id, created_at, follow_up_after_days, source_procedure_visit_id, fee_centavos_override, notes";

  if (sourceProcedureVisitIds.length > 0) {
    const { data, error } = await supabase
      .from("treatment_needs")
      .select(selectFields)
      .in("source_procedure_visit_id", sourceProcedureVisitIds);
    if (error) throw error;
    for (const row of data || []) needsById.set(row.id, row);
  }

  if (visit?.patient_id && visit?.created_at) {
    const startedAt = new Date(visit.created_at);
    const endedAt = new Date(visit.finalized_at || visit.updated_at || visit.created_at);
    if (!Number.isNaN(startedAt.getTime()) && !Number.isNaN(endedAt.getTime())) {
      const rangeEnd = new Date(endedAt.getTime() + 10 * 60 * 1000);
      const { data, error } = await supabase
        .from("treatment_needs")
        .select(selectFields)
        .eq("patient_id", visit.patient_id)
        .gte("created_at", startedAt.toISOString())
        .lte("created_at", rangeEnd.toISOString());
      if (error) throw error;
      for (const row of data || []) needsById.set(row.id, row);
    }
  }

  const rawNeeds = Array.from(needsById.values())
    .filter((need) => !isCompletedNeedForVisit(need, visit?.id))
    .filter((need) => !["Completed", "Declined", "Cancelled", "Voided"].includes(String(need.status || "")));
  if (rawNeeds.length === 0) return [];

  const procedureIds = unique(rawNeeds.map((need) => need.procedure_id).filter(Boolean));
  const planIds = unique(rawNeeds.map((need) => need.treatment_plan_id).filter(Boolean));
  const appointmentIds = unique(rawNeeds.map((need) => need.scheduled_appointment_id).filter(Boolean));
  const [proceduresById, plansById, appointmentsById] = await Promise.all([
    fetchProceduresById(procedureIds),
    fetchPlansById(planIds),
    fetchAppointmentsById(appointmentIds),
  ]);

  return rawNeeds
    .map((need) => {
      const procedure = proceduresById.get(need.procedure_id) || {};
      const plan = plansById.get(need.treatment_plan_id) || {};
      const appointment = appointmentsById.get(need.scheduled_appointment_id) || {};
      return {
        id: need.id,
        procedure_name: procedure.name || "Future need",
        status: need.status || "Open",
        tooth_number: need.tooth_number || null,
        surface: need.surface || null,
        plan_code: plan.plan_code || null,
        treatment_plan_id: need.treatment_plan_id || null,
        treatment_plan_item_id: need.treatment_plan_item_id || null,
        scheduled_appointment_id: need.scheduled_appointment_id || null,
        scheduled_start: appointment.scheduled_start || null,
        appointment_status: appointment.status || null,
        follow_up_after_days: need.follow_up_after_days || null,
        source_procedure_visit_id: need.source_procedure_visit_id || null,
        fee_centavos: Number(need.fee_centavos_override || 0),
        notes: need.notes || null,
        created_at: need.created_at || null,
      };
    })
    .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
}

function isCompletedNeedForVisit(need, visitId) {
  return Boolean(visitId && need.completed_via_visit_id === visitId);
}

async function fetchPaymentAllocations(visit) {
  const events = Array.isArray(visit?.events) ? visit.events : [];
  const paymentIds = events
    .filter((event) => event.event_type === "payment")
    .map((event) => event.id)
    .filter(Boolean);
  if (paymentIds.length === 0) return [];

  const { data: allocationsRaw, error } = await supabase
    .from("money_allocations")
    .select("id, payment_event_id, allocated_to_event_id, amount_centavos, created_at")
    .in("payment_event_id", paymentIds);
  if (error) throw error;
  if (!allocationsRaw?.length) return [];

  const targetIds = unique(allocationsRaw.map((allocation) => allocation.allocated_to_event_id).filter(Boolean));
  const { data: targetsRaw, error: targetsError } = await supabase
    .from("money_events")
    .select("id, event_type, amount_centavos, procedure_id, treatment_plan_id, procedure_visit_id, metadata")
    .in("id", targetIds);
  if (targetsError) throw targetsError;

  const targetsById = new Map((targetsRaw || []).map((event) => [event.id, event]));
  const procedureIds = unique((targetsRaw || []).map((event) => event.procedure_id).filter(Boolean));
  const planIds = unique((targetsRaw || []).map((event) => event.treatment_plan_id).filter(Boolean));
  const [proceduresById, plansById] = await Promise.all([
    fetchProceduresById(procedureIds),
    fetchPlansById(planIds),
  ]);

  const paymentsById = new Map(events.map((event) => [event.id, event]));

  return allocationsRaw.map((allocation) => {
    const target = targetsById.get(allocation.allocated_to_event_id) || {};
    const targetMetadata = target.metadata && typeof target.metadata === "object" ? target.metadata : {};
    const plan = plansById.get(target.treatment_plan_id) || {};
    const procedure = proceduresById.get(target.procedure_id) || {};
    const payment = paymentsById.get(allocation.payment_event_id) || {};
    const isPlanTarget = Boolean(target.treatment_plan_id || targetMetadata.plan_code || targetMetadata.source === "plan_acceptance");
    return {
      id: allocation.id,
      payment_event_id: allocation.payment_event_id,
      allocated_to_event_id: allocation.allocated_to_event_id,
      amount_centavos: Number(allocation.amount_centavos || 0),
      payment_method: payment.payment_method || null,
      target_kind: isPlanTarget ? "tx_plan" : "single",
      target_label: isPlanTarget
        ? `${plan.plan_code || targetMetadata.plan_code || "Tx Plan"} plan charge`
        : procedure.name || targetMetadata.custom_name || "Procedure charge",
      created_at: allocation.created_at || null,
    };
  });
}

async function fetchProceduresById(ids) {
  if (!ids.length) return new Map();
  const { data, error } = await supabase
    .from("procedures")
    .select("id, name")
    .in("id", ids);
  if (error) throw error;
  return new Map((data || []).map((row) => [row.id, row]));
}

async function fetchPlansById(ids) {
  if (!ids.length) return new Map();
  const { data, error } = await supabase
    .from("treatment_plans")
    .select("id, plan_code, status")
    .in("id", ids);
  if (error) throw error;
  return new Map((data || []).map((row) => [row.id, row]));
}

async function fetchAppointmentsById(ids) {
  if (!ids.length) return new Map();
  const { data, error } = await supabase
    .from("appointments")
    .select("id, scheduled_start, status")
    .in("id", ids);
  if (error) throw error;
  return new Map((data || []).map((row) => [row.id, row]));
}

function unique(values) {
  return Array.from(new Set(values.filter(Boolean)));
}

function DetailContent({ visit, enrichment, enrichmentLoading, enrichmentError, buckets }) {
  const isVoided = visit.status === "Voided";
  const events = Array.isArray(visit.events) ? visit.events : [];
  const breakdown = buildVisitBreakdown({ visit, enrichment });
  const others = breakdown.ledgerEvents;
  const receipts = listReceipts(events);
  const adjustments = visit.adjustments_centavos ?? 0;
  const hasDiscount = adjustments > 0;
  // charges_centavos is already net of adjustments per SQL — back out
  // the gross figure so the discount reads as its own line.
  const grossCharges = (visit.charges_centavos ?? 0) + adjustments;
  const [previewReceipt, setPreviewReceipt] = useState(null);

  // Zone 1 data — merge Tx-plan work + single procedures into one plain
  // "what we did" list, no jargon badges, no internal plan codes up front.
  const workItems = [
    ...breakdown.planItemsDone.map((it) => ({
      key: `plan-${it.id}`,
      icon: Layers3,
      iconClass: "text-violet-500",
      name: it.procedure_name,
      detail: [
        it.template_label
          ? `Visit ${it.sequence || ""}: ${it.template_label}`
          : it.sequence ? `Visit ${it.sequence}` : null,
        formatToothScope(it),
      ].filter(Boolean).join(" · "),
      amount: it.fee_centavos,
    })),
    ...breakdown.standaloneProceduresDone.map((p) => ({
      key: `single-${p.id}`,
      icon: ClipboardList,
      iconClass: "text-slate-400",
      name: p.procedure_name,
      detail: formatToothScope(p),
      amount: p.amount_centavos,
    })),
  ];

  const method = derivePrimaryMethod(events);
  // "Where the money went" — how each payment on this visit was applied, in
  // plain terms (today's procedures vs the ongoing plan), plus any refund or
  // adjustment. Replaces the raw money trail.
  const moneyWent = [
    ...breakdown.paymentAllocations.map((a) => {
      const isPlan = a.target_kind === "tx_plan";
      const code = isPlan ? String(a.target_label).split(" ")[0] : null;
      const planLabel = code && code.startsWith("MV") ? "Ongoing treatment" : "Treatment plan";
      return {
        key: `alloc-${a.id}`,
        icon: isPlan ? Layers3 : ClipboardList,
        iconClass: isPlan ? "text-violet-500" : "text-slate-400",
        label: isPlan ? planLabel : a.target_label,
        sub: isPlan ? code : "done today",
        amountText: formatPHP(a.amount_centavos),
        amountClass: "text-emerald-700",
      };
    }),
    ...others.filter((e) => e.event_type !== "payment").map((e) => {
      const d = describeEvent(e);
      return {
        key: `ev-${e.id}`,
        icon: d.icon,
        iconClass: d.color,
        label: d.label,
        sub: null,
        amountText: `${d.sign}${formatPHP(e.amount_centavos)}`,
        amountClass: d.color,
      };
    }),
  ];

  return (
    <div className="p-5 space-y-5">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="font-semibold text-slate-900">
            {formatDate(visit.finalized_at ?? visit.created_at)}
          </span>
          <span className="text-slate-400">·</span>
          <span className="text-slate-700">
            {formatTime(visit.finalized_at ?? visit.created_at)}
          </span>
        </div>
        <p className="text-sm text-slate-700 inline-flex items-center gap-1">
          <Stethoscope className="w-3.5 h-3.5 text-slate-400" />
          {visit.doctor_name}
        </p>
        {isVoided ? (
          <div className="mt-2 bg-rose-50 border border-rose-200 rounded-lg p-2 text-xs text-rose-700">
            <p className="font-semibold">This visit was voided.</p>
            {visit.void_reason ? <p className="mt-0.5">{visit.void_reason}</p> : null}
          </div>
        ) : null}
        {visit.notes ? (
          <p className="text-xs text-slate-600 whitespace-pre-wrap pt-1">
            {visit.notes}
          </p>
        ) : null}
      </div>

      {/* Zone 1 — Treatment */}
      <Zone icon={Stethoscope} title="Treatment" hint="what we did today">
        {enrichmentLoading ? (
          <p className="text-xs text-slate-400 px-1 py-1">Loading…</p>
        ) : workItems.length > 0 ? (
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg bg-white">
            {workItems.map((w) => (
              <li key={w.key} className="px-3 py-2.5 flex items-start justify-between text-sm gap-3">
                <span className="min-w-0 flex gap-2">
                  <w.icon className={`w-4 h-4 mt-0.5 shrink-0 ${w.iconClass}`} />
                  <span className="min-w-0">
                    <span className="font-medium text-slate-900 block truncate">{w.name}</span>
                    {w.detail ? <span className="text-xs text-slate-500">{w.detail}</span> : null}
                  </span>
                </span>
                <span className="font-semibold text-slate-900 shrink-0 tabular-nums">
                  {formatPHP(w.amount)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-slate-400 px-1 py-1 italic">
            No procedures recorded for this visit.
          </p>
        )}
        {breakdown.futureNeedsPlaced.length > 0 ? (
          <div className="mt-2">
            <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Planned next</p>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg bg-white">
              {breakdown.futureNeedsPlaced.map((need) => (
                <FutureNeedRow key={need.id} need={need} />
              ))}
            </ul>
          </div>
        ) : null}
      </Zone>

      {/* Zone 2 — This visit */}
      <Zone icon={WalletCards} title="This visit" hint="charged and paid">
        <div className="border border-slate-200 rounded-lg bg-white px-3.5 py-3 text-sm space-y-1.5">
          <div className="flex justify-between">
            <span className="text-slate-500">{hasDiscount ? "Subtotal" : "Charged"}</span>
            <span>{formatPHP(hasDiscount ? grossCharges : visit.charges_centavos)}</span>
          </div>
          {hasDiscount ? (
            <div className="flex justify-between">
              <span className="text-slate-500">Discount</span>
              <span className="text-amber-600">−{formatPHP(adjustments)}</span>
            </div>
          ) : null}
          <div className="flex justify-between">
            <span className="text-slate-500">Paid{method ? ` · ${method}` : ""}</span>
            <span className="text-emerald-700">−{formatPHP(visit.payments_centavos)}</span>
          </div>
        </div>
      </Zone>

      {/* Zone — Where the money went */}
      {moneyWent.length > 0 ? (
        <Zone icon={ArrowDownCircle} title="Where the money went" hint="how the payment was applied">
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg bg-white">
            {moneyWent.map((m) => (
              <li key={m.key} className="px-3 py-2.5 flex items-center justify-between text-sm gap-3">
                <span className="min-w-0 flex items-center gap-2">
                  <m.icon className={`w-4 h-4 shrink-0 ${m.iconClass}`} />
                  <span className="min-w-0">
                    <span className="text-slate-900 block truncate">{m.label}</span>
                    {m.sub ? <span className="text-xs text-slate-500">{m.sub}</span> : null}
                  </span>
                </span>
                <span className={`font-semibold shrink-0 tabular-nums ${m.amountClass}`}>
                  {m.amountText}
                </span>
              </li>
            ))}
          </ul>
        </Zone>
      ) : null}

      {/* Zone — Account standing */}
      <Zone icon={Scale} title="Account standing" hint="where they stand now">
        <BucketPanel buckets={buckets} />
      </Zone>

      {/* Zone 4 — Documents */}
      {receipts.length > 0 ? (
        <Zone icon={Receipt} title="Documents">
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg bg-white overflow-hidden">
            {receipts.map((r) => (
              <li key={r.eventId}>
                <button
                  type="button"
                  onClick={() => setPreviewReceipt(r)}
                  data-testid={`visit-detail-receipt-${r.number}`}
                  className="w-full px-3 py-2 flex items-center justify-between text-sm gap-3 hover:bg-slate-50 transition-colors text-left"
                >
                  <span className="text-slate-800 inline-flex items-center gap-2 min-w-0">
                    <Receipt className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{r.formatted}</span>
                  </span>
                  <span className="text-[11px] text-slate-500 shrink-0 inline-flex items-center gap-1">
                    {r.date ? formatDate(r.date) : null}
                    <span className="text-teal-600 font-medium">View / print</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Zone>
      ) : null}

      {/* Receipt preview / print modal */}
      {previewReceipt ? (
        <ReceiptPreviewDialog
          visit={visit}
          receipt={previewReceipt}
          planWork={breakdown.planItemsDone}
          onClose={() => setPreviewReceipt(null)}
        />
      ) : null}

      {enrichmentError ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Could not load the full clinical breakdown. The money totals above are still accurate.
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Zone — a labeled block in the visit detail. Each zone has one job:
// Treatment, This visit, Account standing, Documents.
// ---------------------------------------------------------------------
function Zone({ icon: Icon, title, hint, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {hint ? <span className="text-xs text-slate-400">— {hint}</span> : null}
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------
// BucketPanel — the patient's outstanding balance, split into the buckets
// they actually carry (TP / MV / Ortho / Walk-in) plus Total owed. Fed by
// the visit_balance_buckets RPC; the bucket this visit touched is
// highlighted. Buckets with nothing owed and untouched are hidden by the RPC.
// ---------------------------------------------------------------------
function BucketPanel({ buckets }) {
  if (!buckets) {
    return <p className="text-xs text-slate-400 px-1 py-1">Loading balances…</p>;
  }
  const rows = Array.isArray(buckets.buckets) ? buckets.buckets : [];
  const total = buckets.total_owed_centavos ?? 0;
  const totalTone =
    total > 0 ? "text-rose-600" : total < 0 ? "text-emerald-600" : "text-slate-900";

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      {rows.map((b, i) => {
        const bl = balanceLabel(b.balance_centavos);
        const tone =
          bl.tone === "owe" ? "text-rose-600"
          : bl.tone === "credit" ? "text-emerald-600" : "text-slate-400";
        const touched = b.touched_by_visit;
        return (
          <div
            key={`${b.kind}-${b.code || i}`}
            data-testid={`visit-bucket-${b.kind}`}
            className={`flex items-center justify-between px-3.5 py-2.5 border-b border-slate-100 ${touched ? "bg-teal-50" : ""}`}
          >
            <span className="min-w-0">
              <span className={`text-sm ${touched ? "text-teal-800 font-medium" : "text-slate-800"}`}>
                {b.label}
                {b.code ? <span className="ml-1.5 font-mono text-xs text-slate-500">{b.code}</span> : null}
              </span>
              {touched ? (
                <span className="block text-[11px] text-teal-700">this visit charged here</span>
              ) : null}
            </span>
            <span className="text-sm font-semibold tabular-nums shrink-0">
              {b.balance_centavos === 0 ? (
                <span className="text-slate-400">{touched ? "cleared" : "₱0.00"}</span>
              ) : (
                <span className={tone}>
                  {bl.value}{bl.tone === "credit" ? " credit" : ""}
                </span>
              )}
            </span>
          </div>
        );
      })}
      <div className="flex items-center justify-between px-3.5 py-3 bg-slate-50">
        <span className="text-sm font-semibold text-slate-900">Total owed</span>
        <span className={`text-base font-semibold tabular-nums ${totalTone}`}>
          {total === 0
            ? "Paid in full"
            : total < 0
            ? `${formatPHP(-total)} credit`
            : formatPHP(total)}
        </span>
      </div>
    </div>
  );
}

// Primary confirmed payment method(s) for the visit, upper-cased for display.
function derivePrimaryMethod(events) {
  const methods = Array.from(new Set(
    (events || [])
      .filter((e) => e.event_type === "payment" && e.payment_status === "Confirmed" && e.payment_method)
      .map((e) => String(e.payment_method).toUpperCase())
  ));
  if (methods.length === 0) return null;
  if (methods.length === 1) return methods[0];
  return "multiple methods";
}

function FutureNeedRow({ need }) {
  const detail = [
    need.plan_code ? `Tx Plan ${need.plan_code}` : null,
    formatToothScope(need),
    need.follow_up_after_days ? `${need.follow_up_after_days} days after` : null,
    need.scheduled_start ? `Booked ${formatDate(need.scheduled_start)} ${formatTime(need.scheduled_start)}` : null,
  ].filter(Boolean).join(" · ");
  return (
    <li
      className="px-3 py-2.5 flex items-start justify-between text-sm gap-3"
      data-testid={`visit-detail-future-need-${need.id}`}
    >
      <div className="min-w-0 flex gap-2">
        <CalendarPlus className="w-4 h-4 mt-0.5 text-teal-600 shrink-0" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900 truncate">
              {need.procedure_name}
            </span>
            <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold text-teal-700">
              {need.status}
            </span>
          </div>
          {detail ? <p className="mt-0.5 text-xs text-slate-500">{detail}</p> : null}
          {need.notes ? <p className="mt-0.5 text-xs text-slate-500">{need.notes}</p> : null}
        </div>
      </div>
      {need.fee_centavos > 0 ? (
        <span className="font-semibold text-slate-900 shrink-0 tabular-nums">
          {formatPHP(need.fee_centavos)}
        </span>
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------
function describeEvent(e) {
  // Visual cue for direction:
  //   payments are positive money-in (green ↓)
  //   refunds + reversals + write-off-style adjustments are reductions
  //   discounts are reductions on charges (rose ↑ since they cancel out
  //   future payment need)
  if (e.event_type === "payment") {
    const method = (e.payment_method || "").toUpperCase();
    const status =
      e.payment_status && e.payment_status !== "Confirmed"
        ? ` · ${e.payment_status}`
        : "";
    return {
      icon: ArrowDownCircle,
      label: `Payment${method ? ` — ${method}` : ""}${status}`,
      sign: "",
      color: "text-emerald-700",
    };
  }
  if (e.event_type === "refund") {
    return {
      icon: ArrowUpCircle,
      label: "Refund",
      sign: "−",
      color: "text-rose-700",
    };
  }
  if (e.event_type === "adjustment") {
    const type = e.adjustment_type
      ? ` (${e.adjustment_type.replace(/_/g, " ")})`
      : "";
    return {
      icon: Tag,
      label: `Adjustment${type}`,
      sign: "−",
      color: "text-amber-700",
    };
  }
  if (e.event_type === "reversal") {
    return {
      icon: RotateCcw,
      label: "Reversal",
      sign: "−",
      color: "text-slate-500",
    };
  }
  return {
    icon: Tag,
    label: e.event_type,
    sign: "",
    color: "text-slate-700",
  };
}

// Every money_event whose document_type is 'receipt' represents a
// receipt that was issued for this visit. Typically a visit has one
// receipt covering all charges + payments, but the schema allows
// multiple (e.g. partial-payment flows). Group by document_number so
// duplicates collapse, return one row per unique receipt.
function listReceipts(events) {
  const seen = new Map();
  for (const e of events) {
    if (e.document_type !== "receipt") continue;
    if (e.document_number === null || e.document_number === undefined) continue;
    const key = String(e.document_number);
    if (seen.has(key)) continue;
    const year = e.event_date
      ? new Date(e.event_date).getFullYear()
      : new Date().getFullYear();
    seen.set(key, {
      eventId: e.id,
      number: e.document_number,
      formatted: `R-${year}-${String(e.document_number).padStart(6, "0")}`,
      date: e.event_date ?? e.occurred_at ?? null,
    });
  }
  return Array.from(seen.values());
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatPHP(centavos) {
  const n = (centavos ?? 0) / 100;
  return `₱${n.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function formatToothScope(row) {
  if (Array.isArray(row?.teeth) && row.teeth.length > 0) {
    const teeth = row.teeth
      .map((tooth) => {
        const number = tooth?.tooth_number || tooth?.number;
        if (!number) return null;
        const role = tooth?.role ? ` ${String(tooth.role).slice(0, 1).toUpperCase()}` : "";
        return `#${number}${role}`;
      })
      .filter(Boolean);
    if (teeth.length > 0) {
      const units = row.unit_count ? `${row.unit_count} unit${row.unit_count === 1 ? "" : "s"}` : null;
      return [teeth.join(", "), units].filter(Boolean).join(" · ");
    }
  }
  const tooth = row?.tooth_number ? `#${row.tooth_number}` : "";
  const surface = row?.surface ? `surface ${row.surface}` : "";
  return [tooth, surface].filter(Boolean).join(" · ");
}

// ---------------------------------------------------------------------
// ReceiptPreviewDialog — sub-modal that renders a print-friendly
// receipt for one document_number on this visit. Visible on screen as
// a preview; the Print button calls window.print() and the print
// stylesheet hides everything except the receipt portal so the
// printed output is one clean page.
// ---------------------------------------------------------------------
function ReceiptPreviewDialog({ visit, receipt, planWork = [], onClose }) {
  const { activeClinic } = useAuth();
  const [letterhead, setLetterhead] = useState({ logo_url: null, address: null, phone: null, email: null });
  const [receiptSignatureDoc, setReceiptSignatureDoc] = useState(null);
  const [receiptSignOpen, setReceiptSignOpen] = useState(false);
  const [receiptSignatureBusy, setReceiptSignatureBusy] = useState(false);
  // Plan itemization: a charge that is a whole plan/MV bundle (e.g. the
  // ₱86,000 "Procedure" line) gets broken into its component procedures so
  // the receipt reads like a professional itemized bill, not one lump line.
  const [planByCharge, setPlanByCharge] = useState({});
  // Payment made on this visit but applied to OTHER balances (e.g. the
  // ongoing plan) — so the receipt shows where an overpayment actually
  // went instead of mislabeling it "Credit".
  const [appliedElsewhere, setAppliedElsewhere] = useState([]);

  useEffect(() => {
    const evs = Array.isArray(visit?.events) ? visit.events : [];
    const paymentIds = evs
      .filter((e) => e.event_type === "payment" && e.payment_status === "Confirmed")
      .map((e) => e.id);
    if (!paymentIds.length) return undefined;
    const ownChargeIds = new Set(evs.filter((e) => e.event_type === "charge").map((e) => e.id));
    let cancelled = false;
    (async () => {
      const { data: allocs } = await supabase
        .from("money_allocations")
        .select("allocated_to_event_id, amount_centavos")
        .in("payment_event_id", paymentIds);
      if (cancelled || !allocs?.length) return;
      const targetIds = [...new Set(allocs.map((a) => a.allocated_to_event_id).filter(Boolean))];
      const { data: targets } = await supabase
        .from("money_events")
        .select("id, treatment_plan_id")
        .in("id", targetIds);
      const targetById = new Map((targets || []).map((t) => [t.id, t]));
      const byPlan = new Map();
      for (const a of allocs) {
        const t = targetById.get(a.allocated_to_event_id);
        // only allocations that left THIS visit and landed on a plan
        if (!t || ownChargeIds.has(t.id) || !t.treatment_plan_id) continue;
        byPlan.set(t.treatment_plan_id, (byPlan.get(t.treatment_plan_id) || 0) + Number(a.amount_centavos || 0));
      }
      const planIds = [...byPlan.keys()];
      if (!planIds.length) { if (!cancelled) setAppliedElsewhere([]); return; }
      const { data: plans } = await supabase
        .from("treatment_plans").select("id, plan_code, is_informal").in("id", planIds);
      const meta = new Map((plans || []).map((p) => [p.id, p]));
      const list = planIds.map((pid) => ({
        code: meta.get(pid)?.plan_code || "Plan",
        is_informal: meta.get(pid)?.is_informal,
        amount: byPlan.get(pid),
      }));
      if (!cancelled) setAppliedElsewhere(list);
    })();
    return () => { cancelled = true; };
  }, [visit?.id, visit?.events]);

  useEffect(() => {
    if (!visit?.id) return undefined;
    let cancelled = false;
    (async () => {
      // charge events on this visit that point at a treatment plan
      const { data: chargeRows } = await supabase
        .from("money_events")
        .select("id, treatment_plan_id, amount_centavos")
        .eq("visit_id", visit.id)
        .eq("event_type", "charge")
        .not("treatment_plan_id", "is", null);
      if (cancelled || !chargeRows?.length) return;

      const planIds = [...new Set(chargeRows.map((r) => r.treatment_plan_id))];
      const [{ data: plans }, { data: items }] = await Promise.all([
        supabase.from("treatment_plans").select("id, plan_code, is_informal").in("id", planIds),
        supabase
          .from("treatment_plan_items")
          .select("treatment_plan_id, procedure_id, name_snapshot, committed_fee_centavos, fee_centavos, tooth_number")
          .in("treatment_plan_id", planIds),
      ]);
      const procIds = [...new Set((items || []).map((i) => i.procedure_id).filter(Boolean))];
      const { data: procs } = procIds.length
        ? await supabase.from("procedures").select("id, name").in("id", procIds)
        : { data: [] };
      const procName = new Map((procs || []).map((p) => [p.id, p.name]));
      const planMeta = new Map((plans || []).map((p) => [p.id, p]));
      const itemsByPlan = new Map();
      for (const it of items || []) {
        const list = itemsByPlan.get(it.treatment_plan_id) || [];
        list.push({
          name: it.name_snapshot || procName.get(it.procedure_id) || "Procedure",
          tooth: it.tooth_number || null,
          fee: Number(it.committed_fee_centavos ?? it.fee_centavos ?? 0),
        });
        itemsByPlan.set(it.treatment_plan_id, list);
      }
      const map = {};
      for (const row of chargeRows) {
        const meta = planMeta.get(row.treatment_plan_id);
        const list = itemsByPlan.get(row.treatment_plan_id) || [];
        if (meta && list.length > 0) {
          map[row.id] = {
            code: meta.plan_code,
            is_informal: meta.is_informal,
            items: list,
          };
        }
      }
      if (!cancelled) setPlanByCharge(map);
    })();
    return () => { cancelled = true; };
  }, [visit?.id]);

  useEffect(() => {
    if (!activeClinic?.clinic_id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("clinic_settings")
        .select("logo_url, address, phone, email")
        .eq("clinic_id", activeClinic.clinic_id)
        .maybeSingle();
      if (!cancelled && data) setLetterhead(data);
    })();
    return () => { cancelled = true; };
  }, [activeClinic?.clinic_id]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const events = Array.isArray(visit.events) ? visit.events : [];
  const charges = events.filter((e) => e.event_type === "charge");
  const payments = events.filter((e) => e.event_type === "payment" && e.payment_status === "Confirmed");
  const adjustmentsList = events.filter((e) => e.event_type === "adjustment");
  const refunds = events.filter((e) => e.event_type === "refund");
  const adjustments = visit.adjustments_centavos ?? 0;
  const grossCharges = (visit.charges_centavos ?? 0) + adjustments;
  // Plan/MV work performed this visit but billed via the plan (₱0 charged
  // today). Show it on the receipt so cementations etc. aren't invisible —
  // but skip plans already itemized as a charge line here (acceptance visit),
  // to avoid listing the same procedure twice.
  const itemizedPlanCodes = new Set(Object.values(planByCharge).map((p) => p.code));
  const planWorkRows = (planWork || []).filter((w) => !itemizedPlanCodes.has(w.plan_code));
  const clinicName = activeClinic?.clinic_name || "Dental Clinic";
  const patientName = `${visit.patient_first_name ?? ""} ${visit.patient_last_name ?? ""}`.trim() || "Patient";
  const receiptSignature = signatureForPdf(receiptSignatureDoc);
  const receiptSigned = !!receiptSignature;
  const canSignReceipt = !!visit?.id && !!receipt?.formatted && visit.status !== "Voided" && !receiptSigned;

  useEffect(() => {
    let cancelled = false;
    if (!visit?.id) {
      setReceiptSignatureDoc(null);
      return () => { cancelled = true; };
    }
    getSignedDocumentStatus({
      documentType: DOCUMENT_TYPES.checkoutReceipt,
      sourceId: visit.id,
    }).then((doc) => {
      if (!cancelled) setReceiptSignatureDoc(doc || null);
    }).catch((err) => {
      if (!cancelled) {
        console.warn("[visits] receipt signature status failed", err);
        setReceiptSignatureDoc(null);
      }
    });
    return () => { cancelled = true; };
  }, [visit?.id]);

  const handleReceiptSignatureSubmit = useCallback(async (signature) => {
    if (!visit?.id) return;
    setReceiptSignatureBusy(true);
    try {
      const snapshot = await createDocumentSnapshot({
        documentType: DOCUMENT_TYPES.checkoutReceipt,
        sourceTable: "visits",
        sourceId: visit.id,
        patientId: visit.patient_id || null,
        documentNumber: receipt.formatted || null,
        documentTitle: "Checkout Receipt",
        documentVersion: "patient_visit_receipt_preview_v1",
        signatureAnchor: { page: "last", slot: "receipt_acknowledgment" },
        documentPayload: {
          visit,
          receipt,
          patient: { id: visit.patient_id || null, name: patientName },
          clinic: {
            id: activeClinic?.clinic_id || null,
            name: clinicName,
          },
          charges,
          payments,
          adjustments: adjustmentsList,
          refunds,
          summary: {
            gross_charges_centavos: grossCharges,
            net_charges_centavos: visit.charges_centavos ?? 0,
            adjustments_centavos: adjustments,
            payments_centavos: visit.payments_centavos ?? 0,
            balance_centavos: visit.balance_centavos ?? 0,
          },
        },
      });
      const signed = await signDocumentSnapshot({
        documentId: snapshot?.id,
        signature,
      });
      setReceiptSignatureDoc(signed || null);
      setReceiptSignOpen(false);
      toast.success("Receipt signed.");
    } catch (err) {
      toast.error(err?.message || "Could not sign receipt.");
      console.warn("[visits] receipt signature failed", err);
    } finally {
      setReceiptSignatureBusy(false);
    }
  }, [
    activeClinic?.clinic_id,
    adjustments,
    adjustmentsList,
    charges,
    clinicName,
    grossCharges,
    patientName,
    payments,
    receipt,
    refunds,
    visit,
  ]);

  function handlePrint() { window.print(); }

  // Render through a portal so the print CSS can target body's direct
  // children — same pattern as the existing checkout ReceiptPrint.
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="dos-receipt-preview-portal">
      <style>{`
        @media screen {
          .dos-receipt-overlay {
            position: fixed; inset: 0; z-index: 60;
            background: rgba(15, 23, 42, 0.55);
            display: flex; align-items: center; justify-content: center;
            padding: 16px;
          }
          .dos-receipt-card {
            background: #fff; border-radius: 16px;
            box-shadow: 0 20px 50px rgba(0,0,0,0.25);
            max-width: 640px; width: 100%; max-height: 90vh;
            display: flex; flex-direction: column;
          }
          .dos-receipt-header {
            display: flex; align-items: center; justify-content: space-between;
            padding: 12px 16px; border-bottom: 1px solid #f1f5f9;
            position: sticky; top: 0; background: #fff; z-index: 1;
            border-top-left-radius: 16px; border-top-right-radius: 16px;
          }
          .dos-receipt-body { overflow-y: auto; padding: 24px; }
        }
        @media print {
          body > *:not(.dos-receipt-preview-portal) { display: none !important; }
          .dos-receipt-preview-portal { display: block !important; }
          .dos-receipt-overlay {
            background: #fff !important; padding: 0 !important; position: static !important;
          }
          .dos-receipt-card {
            box-shadow: none !important; max-width: 100% !important;
            max-height: none !important; border-radius: 0 !important;
          }
          .dos-receipt-header { display: none !important; }
          .dos-receipt-body { padding: 0 !important; overflow: visible !important; }
          html, body { background: #fff !important; }
          @page { margin: 12mm; size: A4; }
        }

        .dos-receipt {
          font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          color: #0f172a; font-size: 12px; line-height: 1.5;
        }
        .dos-receipt header.r {
          display: flex; justify-content: space-between; align-items: flex-start;
          border-bottom: 1.5pt solid #cbd5e1;
          padding-bottom: 10px; margin-bottom: 16px;
        }
        .dos-receipt h1 { font-size: 16px; margin: 0; font-weight: 700; }
        .dos-receipt .clinic-meta {
          font-size: 10px; color: #64748b; margin-top: 4px;
          white-space: pre-wrap;
        }
        .dos-receipt .meta-r { text-align: right; }
        .dos-receipt .meta-r .label {
          font-size: 9px; text-transform: uppercase; letter-spacing: .04em;
          color: #64748b; font-weight: 600;
        }
        .dos-receipt .meta-r .num {
          font-size: 13px; font-weight: 700; margin-top: 2px;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        }
        .dos-receipt .meta-r .when { color: #64748b; margin-top: 4px; font-size: 10px; }
        .dos-receipt .pp { display: flex; gap: 28px; margin-bottom: 16px; }
        .dos-receipt .pp .label {
          font-size: 9px; text-transform: uppercase; letter-spacing: .04em;
          color: #64748b; font-weight: 600;
        }
        .dos-receipt .pp .name { font-weight: 600; margin-top: 2px; }
        .dos-receipt table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
        .dos-receipt thead th {
          font-size: 9px; text-transform: uppercase; letter-spacing: .04em;
          color: #64748b; font-weight: 600;
          border-bottom: 1.5pt solid #cbd5e1;
          padding: 4px; text-align: left;
        }
        .dos-receipt thead th.num { text-align: right; }
        .dos-receipt tbody td { padding: 5px 4px; border-bottom: .75pt solid #e2e8f0; }
        .dos-receipt td.num { text-align: right; font-variant-numeric: tabular-nums; }
        .dos-receipt td.amt { font-weight: 600; }
        .dos-receipt .totals { margin-left: auto; width: 240px; margin-top: 8px; }
        .dos-receipt .totals .row { display: flex; justify-content: space-between; padding: 2px 0; }
        .dos-receipt .totals .dim { color: #64748b; }
        .dos-receipt .totals .total {
          border-top: 1.5pt solid #cbd5e1; padding-top: 4px; margin-top: 4px;
          font-weight: 700; font-size: 13px;
        }
        .dos-receipt .totals .balance { color: #b45309; font-weight: 600; }
        .dos-receipt .pay-list { margin-top: 18px; }
        .dos-receipt .pay-list .row {
          display: flex; justify-content: space-between; padding: 3px 0;
          border-bottom: .5pt dashed #e2e8f0;
        }
        .dos-receipt footer.r {
          text-align: center; font-size: 9px; color: #94a3b8;
          border-top: .75pt solid #e2e8f0; padding-top: 12px; margin-top: 22px;
        }
        .dos-receipt .voided-banner {
          background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c;
          padding: 8px 10px; border-radius: 6px; font-size: 11px;
          margin-bottom: 14px;
        }
        .dos-receipt .signature-grid { display: flex; gap: 32px; margin-top: 20px; }
        .dos-receipt .signature-col { flex: 1; }
        .dos-receipt .signature-pad { height: 34px; display: flex; align-items: flex-end; }
        .dos-receipt .signature-pad img { max-width: 150px; max-height: 34px; object-fit: contain; }
        .dos-receipt .signature-line { border-top: .75pt solid #0f172a; padding-top: 4px; }
        .dos-receipt .signature-name { font-weight: 600; }
        .dos-receipt .signature-role { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: .04em; margin-top: 2px; }
        .dos-receipt .signature-witness { font-size: 9px; color: #64748b; margin-top: 3px; }
      `}</style>

      <div className="dos-receipt-overlay" onClick={onClose}>
        <div
          className="dos-receipt-card"
          onClick={(e) => e.stopPropagation()}
          data-testid="visit-receipt-preview"
        >
          <div className="dos-receipt-header">
            <h2 className="text-base font-semibold text-slate-900">{receipt.formatted}</h2>
            <div className="flex items-center gap-1">
              {canSignReceipt ? (
                <button
                  type="button"
                  onClick={() => setReceiptSignOpen(true)}
                  disabled={receiptSignatureBusy}
                  data-testid="visit-receipt-sign"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-teal-50 text-teal-800 border border-teal-200 rounded-md hover:bg-teal-100 disabled:opacity-50"
                >
                  {receiptSignatureBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PenLine className="w-3.5 h-3.5" />}
                  Sign receipt
                </button>
              ) : receiptSigned ? (
                <span
                  data-testid="visit-receipt-signed"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-md"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Receipt signed
                </span>
              ) : null}
              <button
                onClick={handlePrint}
                data-testid="visit-receipt-print"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-teal-600 text-white rounded-md hover:bg-teal-700"
              >
                <Printer className="w-3.5 h-3.5" /> Print / Save PDF
              </button>
              <button
                onClick={onClose}
                aria-label="Close"
                className="text-slate-400 hover:text-slate-700 p-1.5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="dos-receipt-body">
            <div className="dos-receipt">
              <header className="r">
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  {letterhead.logo_url ? (
                    <img
                      src={letterhead.logo_url}
                      alt=""
                      style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 6 }}
                    />
                  ) : null}
                  <div>
                    <h1>{clinicName}</h1>
                    {letterhead.address ? <p className="clinic-meta">{letterhead.address}</p> : null}
                    {(letterhead.phone || letterhead.email) ? (
                      <p className="clinic-meta">
                        {[letterhead.phone, letterhead.email].filter(Boolean).join(" · ")}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="meta-r">
                  <p className="label">Receipt</p>
                  <p className="num">{receipt.formatted}</p>
                  <p className="when">
                    {receipt.date ? new Date(receipt.date).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : null}
                  </p>
                </div>
              </header>

              {visit.status === "Voided" ? (
                <div className="voided-banner">
                  This visit was voided{visit.void_reason ? `: ${visit.void_reason}` : "."}
                </div>
              ) : null}

              <section className="pp">
                <div>
                  <p className="label">Patient</p>
                  <p className="name">{patientName}</p>
                </div>
                <div>
                  <p className="label">Doctor</p>
                  <p className="name">{visit.doctor_name || "Unassigned"}</p>
                </div>
                <div>
                  <p className="label">Visit date</p>
                  <p className="name">
                    {visit.finalized_at
                      ? new Date(visit.finalized_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                      : "—"}
                  </p>
                </div>
              </section>

              {charges.length > 0 || planWorkRows.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th>Procedure</th>
                      <th className="num" style={{ width: 100 }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {charges.map((c) => {
                      const plan = planByCharge[c.id];
                      if (plan) {
                        const label = plan.is_informal ? "Multi-visit plan" : "Treatment plan";
                        return (
                          <Fragment key={c.id}>
                            <tr>
                              <td style={{ fontWeight: 600 }}>{label} · {plan.code}</td>
                              <td className="num amt">{formatPHP(c.amount_centavos)}</td>
                            </tr>
                            {plan.items.map((it, i) => (
                              <tr key={`${c.id}-${i}`}>
                                <td style={{ paddingLeft: 18, color: "#475569" }}>
                                  {it.name}{it.tooth ? ` · #${it.tooth}` : ""}
                                </td>
                                <td className="num" style={{ color: "#475569" }}>{formatPHP(it.fee)}</td>
                              </tr>
                            ))}
                          </Fragment>
                        );
                      }
                      return (
                        <tr key={c.id}>
                          <td>{c.procedure_name ?? "Procedure"}</td>
                          <td className="num amt">{formatPHP(c.amount_centavos)}</td>
                        </tr>
                      );
                    })}
                    {planWorkRows.map((w) => {
                      const tooth = formatToothScope(w);
                      const detail = [w.template_label || (w.sequence ? `Visit ${w.sequence}` : null), tooth]
                        .filter(Boolean).join(" · ");
                      return (
                        <tr key={`pw-${w.id}`}>
                          <td>{w.procedure_name}{detail ? ` · ${detail}` : ""}</td>
                          <td className="num" style={{ color: "#64748b", fontStyle: "italic" }}>
                            on plan · {w.plan_code}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p style={{ color: "#94a3b8", fontStyle: "italic" }}>No procedures recorded.</p>
              )}

              <div className="totals">
                <div className="row"><span>Subtotal</span><span>{formatPHP(grossCharges)}</span></div>
                {adjustments > 0 ? (
                  <div className="row dim">
                    <span>Discount</span>
                    <span>−{formatPHP(adjustments)}</span>
                  </div>
                ) : null}
                <div className="row total">
                  <span>Total</span>
                  <span>{formatPHP(visit.charges_centavos)}</span>
                </div>
                <div className="row dim">
                  <span>Paid</span>
                  <span>{formatPHP(visit.payments_centavos)}</span>
                </div>
                {appliedElsewhere.map((ae) => (
                  <div className="row dim" key={ae.code}>
                    <span>Applied to {ae.is_informal ? "ongoing treatment" : "treatment plan"} · {ae.code}</span>
                    <span>{formatPHP(ae.amount)}</span>
                  </div>
                ))}
                {(() => {
                  const bal = visit.balance_centavos ?? 0;
                  const appliedTotal = appliedElsewhere.reduce((s, a) => s + a.amount, 0);
                  if (bal > 0) {
                    return (
                      <div className="row balance">
                        <span>Balance due</span>
                        <span>{formatPHP(bal)}</span>
                      </div>
                    );
                  }
                  // leftover overpayment after everything the payment was applied to
                  const credit = -bal - appliedTotal;
                  if (credit > 0) {
                    return (
                      <div className="row balance">
                        <span>Credit</span>
                        <span>{formatPHP(credit)}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {payments.length > 0 || refunds.length > 0 || adjustmentsList.length > 0 ? (
                <section className="pay-list">
                  <p className="label" style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".04em", color: "#64748b", fontWeight: 600, marginBottom: 6 }}>
                    Payment activity
                  </p>
                  {payments.map((p) => (
                    <div className="row" key={p.id}>
                      <span>
                        {(p.payment_method || "Payment").toUpperCase()}
                        {p.event_date ? ` · ${new Date(p.event_date).toLocaleDateString()}` : ""}
                      </span>
                      <span>{formatPHP(p.amount_centavos)}</span>
                    </div>
                  ))}
                  {adjustmentsList.map((a) => (
                    <div className="row dim" key={a.id}>
                      <span>{a.adjustment_type ? `Adjustment (${a.adjustment_type.replace(/_/g, " ")})` : "Adjustment"}</span>
                      <span>−{formatPHP(a.amount_centavos)}</span>
                    </div>
                  ))}
                  {refunds.map((rf) => (
                    <div className="row" key={rf.id}>
                      <span>Refund</span>
                      <span>−{formatPHP(rf.amount_centavos)}</span>
                    </div>
                  ))}
                </section>
              ) : null}

              <section className="signature-grid">
                <div className="signature-col">
                  <div className="signature-pad">
                    {receiptSignature?.imageDataUrl ? (
                      <img src={receiptSignature.imageDataUrl} alt="" />
                    ) : null}
                  </div>
                  <div className="signature-line">
                    <p className="signature-name">{receiptSignature?.signerName || patientName}</p>
                    <p className="signature-role">
                      {receiptSignature?.signerRole && receiptSignature.signerRole !== "patient"
                        ? receiptSignature.signerRole
                        : "Patient"}
                    </p>
                    {receiptSignature?.staffWitnessName ? (
                      <p className="signature-witness">Witnessed by {receiptSignature.staffWitnessName}</p>
                    ) : null}
                  </div>
                </div>
                <div className="signature-col">
                  <div className="signature-pad" />
                  <div className="signature-line">
                    <p className="signature-name">{clinicName}</p>
                    <p className="signature-role">Clinic copy acknowledgment</p>
                  </div>
                </div>
              </section>

              <footer className="r">Thank you for visiting {clinicName}.</footer>
            </div>
          </div>
        </div>
      </div>
      <SignatureDialog
        open={receiptSignOpen}
        onOpenChange={(next) => {
          if (!next && !receiptSignatureBusy) setReceiptSignOpen(false);
        }}
        context={{
          title: "Sign checkout receipt",
          documentNumber: receipt.formatted,
          patientName,
        }}
        defaultSignerName={patientName}
        busy={receiptSignatureBusy}
        onSubmit={handleReceiptSignatureSubmit}
      />
    </div>,
    document.body,
  );
}
